import { useEffect } from "react";
import { useStore } from "../context/StoreContext";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

/** Default iRepair-blue storefront favicon — matches the sidebar logo used
 *  on the POS counter. Phosphor Storefront (regular) rendered on a rounded
 *  blue tile so it's recognisable at 16×16 in the browser tab. */
const DEFAULT_FAVICON =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">
       <rect width="64" height="64" rx="14" fill="#1E5FEB"/>
       <g stroke="#fff" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round" fill="none">
         <path d="M14 28 L18 16 H46 L50 28"/>
         <path d="M14 28 Q17 32 20 28 Q23 32 26 28 Q29 32 32 28 Q35 32 38 28 Q41 32 44 28 Q47 32 50 28"/>
         <path d="M18 30 V48 H46 V30"/>
         <rect x="28" y="37" width="8" height="11"/>
       </g>
     </svg>`
  );

const setFavicon = (href) => {
  let link = document.querySelector('link[rel="icon"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.type = href.startsWith("data:image/svg") ? "image/svg+xml" : "image/png";
  link.href = href;
};

/** Dynamically sets the browser tab favicon + title based on the active store.
 *  - Uses the store's uploaded logo (from Settings → Templates) when present.
 *  - Falls back to a built-in iRepair mark.
 *  - Updates <title> to "<Store name> · iRepair POS".
 *  Mount once inside <StoreProvider>. */
export default function StoreFavicon() {
  const { user } = useAuth();
  const { currentStore } = useStore();

  useEffect(() => {
    if (!currentStore?.id || !user) {
      setFavicon(DEFAULT_FAVICON);
      document.title = "iRepair POS";
      return;
    }
    document.title = `${currentStore.name} · iRepair POS`;
    // Fetch the store's template to pull the uploaded logo. Fall back to the
    // default mark when unset or the request fails.
    let cancelled = false;
    api
      .get(`/stores/${currentStore.id}/templates`)
      .then(({ data }) => {
        if (cancelled) return;
        // Prefer the dedicated favicon. We intentionally DO NOT fall back to
        // the receipt logo because the two assets have different aspect ratios
        // and resolutions (logo = wide & high-res; favicon = 32×32 square).
        const fav = (data?.favicon_data_url || "").trim();
        setFavicon(fav || DEFAULT_FAVICON);
      })
      .catch(() => {
        if (!cancelled) setFavicon(DEFAULT_FAVICON);
      });
    return () => { cancelled = true; };
  }, [currentStore?.id, currentStore?.name, user]);

  return null;
}
